import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppBadIcon from "@mui/icons-material/GppBad";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import classes from "./verification-icons.module.scss";

// Visual language for the WC / 1M Appointment columns. Built entirely on
// stock Material icons (already a dependency via @mui/icons-material, and
// already used elsewhere in this file/DetailCard.jsx) rather than
// hand-drawn custom shapes - real, widely-recognized icon glyphs read as
// "designed", not "generated", and the semantics line up almost exactly:
//   - GppGood / GppBad / GppMaybe ("shield ok / bad / unsure") is
//     literally Google's own verified-status icon family - a perfect
//     match for "confirmed yes / confirmed no / no clear answer".
//   - EventAvailable / EventBusy / a plain outlined calendar covers the
//     same three states for the appointment check.
// Three live states each (confirmed yes, confirmed no, no response) plus
// "not yet checked at all" (no verification record exists - handled by
// the callers below returning null before any glyph renders).
//
// Both signals come off the same verification record
// (acceptsWorkersComp / appointmentWithinMonth) but are read
// independently - a "no" or "no response" on one doesn't blank out the
// other, since they're two separate answers from the same call.

export const STATE_COLOR = {
  yes: "#0e8f82",
  no: "#c0392b",
  "no-response": "#b3790a",
};

// Light, white-card style - matches the rest of the app's tooltips
// (the "Modus rating" info tooltip in ProviderCard.jsx, and the legend
// popover below) instead of a one-off dark box that reads as a
// different, unrelated UI language.
export const TOOLTIP_SX = {
  tooltip: {
    sx: {
      bgcolor: "#ffffff",
      color: "#1c1c1e",
      border: "1px solid #e5e7eb",
      boxShadow: "0 10px 24px rgba(10, 21, 38, 0.14)",
      fontSize: "11.5px",
      lineHeight: 1.5,
      padding: "10px 13px",
      borderRadius: "9px",
      maxWidth: 220,
      "& b": { display: "block", fontSize: "12px", marginBottom: "3px", color: "#1c1c1e" },
    },
  },
  arrow: {
    sx: {
      color: "#ffffff",
      "&::before": { border: "1px solid #e5e7eb" },
    },
  },
};

export function formatVerificationDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// null (not yet checked) is handled by the caller returning nothing, so
// this only ever needs to resolve to one of these three.
export function getConfirmedState(value) {
  if (value === "yes") return "yes";
  if (value === "no") return "no";
  return "no-response";
}

export function ShieldGlyph({ state, size = 27 }) {
  const sx = { fontSize: size, color: STATE_COLOR[state] };
  if (state === "yes") return <GppGoodIcon sx={sx} aria-hidden="true" />;
  if (state === "no") return <GppBadIcon sx={sx} aria-hidden="true" />;
  return <GppMaybeIcon sx={sx} aria-hidden="true" />;
}

export function CalendarGlyph({ state, size = 24 }) {
  const sx = { fontSize: size, color: STATE_COLOR[state] };
  if (state === "yes") return <EventAvailableIcon sx={sx} aria-hidden="true" />;
  if (state === "no") return <EventBusyIcon sx={sx} aria-hidden="true" />;
  return <EventOutlinedIcon sx={sx} aria-hidden="true" />;
}

export const WC_TOOLTIP_COPY = {
  yes: { title: "Accepts WC", body: "Sophia AI called and confirmed this practice accepts Workers' Comp patients." },
  no: { title: "Does not accept WC", body: "Sophia AI called and confirmed this practice does not take Workers' Comp. The call succeeded - the answer was no." },
  "no-response": { title: "No response", body: "Sophia AI called but didn't get a clear answer. Status is unconfirmed - worth a retry." },
};

export const APPT_TOOLTIP_COPY = {
  yes: { title: "Available", body: "Confirmed an opening within the next month." },
  no: { title: "Not available", body: "Confirmed nothing open within the next month. A confirmed answer, not a guess." },
  "no-response": { title: "No response", body: "The call didn't get a clear answer on appointment availability specifically." },
};

// Copy for the standalone legend popover (VerificationInfoButton below) -
// short labels rather than full sentences, since these sit next to an
// actual rendered icon instead of standing alone.
const WC_LEGEND = [
  { state: "yes", label: "Accepts Workers' Comp" },
  { state: "no", label: "Does not accept Workers' Comp" },
  { state: "no-response", label: "Call didn't get a clear answer" },
];

const APPT_LEGEND = [
  { state: "yes", label: "Opening within 1 month" },
  { state: "no", label: "No opening within 1 month" },
  { state: "no-response", label: "Call didn't get a clear answer" },
];

export function WcShieldIcon({ verification, size }) {
  if (!verification) return null;
  const state = getConfirmedState(verification.acceptsWorkersComp);
  const copy = WC_TOOLTIP_COPY[state];
  const date = formatVerificationDate(verification.createdAtUtc);

  return (
    <Tooltip
      arrow
      componentsProps={TOOLTIP_SX}
      title={
        <>
          <b>{copy.title}</b>
          {copy.body}
          {date ? ` (${date})` : ""}
        </>
      }
    >
      <div className={classes["glyph-wrap"]}>
        <ShieldGlyph state={state} size={size} />
        {date && <span className={classes["glyph-date"]}>{date}</span>}
      </div>
    </Tooltip>
  );
}

export function ApptCalendarIcon({ verification, size }) {
  if (!verification) return null;
  const state = getConfirmedState(verification.appointmentWithinMonth);
  const copy = APPT_TOOLTIP_COPY[state];
  const date = formatVerificationDate(verification.createdAtUtc);

  return (
    <Tooltip
      arrow
      componentsProps={TOOLTIP_SX}
      title={
        <>
          <b>{copy.title}</b>
          {copy.body}
          {date ? ` (${date})` : ""}
        </>
      }
    >
      <div className={classes["glyph-wrap"]}>
        <CalendarGlyph state={state} size={size} />
        {date && <span className={classes["glyph-date"]}>{date}</span>}
      </div>
    </Tooltip>
  );
}

// Standalone "(i)" trigger that explains what the WC / 1M Apmt icons mean
// - lives in the table's toolbar (next to "Show flagged") rather than on
// any one cell, since it documents the whole column, not one row's
// result. Renders the *actual* glyph components at legend size so the
// explanation always matches what's really drawn in the table, instead
// of a second hand-maintained description that could drift out of sync.
export function VerificationInfoButton() {
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            color: "#1c1c1e",
            border: "1px solid #e5e7eb",
            boxShadow: "0 12px 28px rgba(10, 21, 38, 0.16)",
            borderRadius: "10px",
            padding: "12px 14px",
            maxWidth: 300,
            "& b": { display: "block", fontSize: "12.5px", marginBottom: "8px" },
          },
        },
        arrow: {
          sx: {
            color: "#ffffff",
            "&::before": { border: "1px solid #e5e7eb" },
          },
        },
      }}
      title={
        <div className={classes["verification-legend"]}>
          <b>What do the WC and 1M Apmt icons mean?</b>
          <div className={classes["verification-legend-col"]}>
            <span className={classes["verification-legend-heading"]}>WC - Workers' Comp</span>
            {WC_LEGEND.map(({ state, label }) => (
              <div key={`wc-${state}`} className={classes["verification-legend-row"]}>
                <span className={classes["verification-legend-icon"]}>
                  <ShieldGlyph state={state} size={20} />
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>
          <div className={classes["verification-legend-col"]}>
            <span className={classes["verification-legend-heading"]}>1M Apmt - New-patient opening</span>
            {APPT_LEGEND.map(({ state, label }) => (
              <div key={`apt-${state}`} className={classes["verification-legend-row"]}>
                <span className={classes["verification-legend-icon"]}>
                  <CalendarGlyph state={state} size={20} />
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>
          <span className={classes["verification-legend-footer"]}>
            Blank in either column means Sophia AI hasn't called this practice yet.
          </span>
        </div>
      }
    >
      <button
        type="button"
        className={classes["verification-info-btn"]}
        aria-label="What do the WC and 1M Apmt icons mean?"
      >
        <InfoOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
    </Tooltip>
  );
}
